import {useEffect, useRef, useState} from 'react';
import MainCard from './components/video/MainCard';
import {PERSONAS} from './config/personas';
import {ACCOUNT_ID} from './api/account';
import {createVideoGeneration} from './api/video';
import {useAccountStatus} from './hooks/useAccountStatus';
import {trackEvent} from './utils/analytics';
import {showError} from './utils/notify';
import MusicCard from './components/music/MusicCard';
import Header from './components/layout/Header';
import HeroSection from './components/landing/HeroSection';
import WhyPerfectSection from './components/landing/WhyPerfectSection';
import FiveReasonsSection from './components/landing/FiveReasonsSection';
import Paywall from './components/paywall/Paywall';
import MyContentPage from './components/pages/MyContentPage';
import LinkAccountButton from './components/account/LinkAccountButton';
import VideoModal from './components/video/VideoModal';
import Footer from './components/layout/Footer';
import StarReviewSection from './components/landing/StarReviewSection';
import {GENERATION_STATUS_TEXTS} from './config/constants';
import {getPlans} from './api/plans';
import {
    getPageConfig,
    MUSIC_FIVE_REASONS,
    MUSIC_HERO,
    MUSIC_WHY_PERFECT
} from './config/pageConfigs';
import {blogArticles} from './config/blogArticles';
import SkazkiCard from './components/skazki/SkazkiCard';
import {useSkazkiAudio} from './utils/skazkiAudio';

function pluralizeSongs(count) {
    const mod10 = count % 10;
    const mod100 = count % 100;
    if (mod10 === 1 && mod100 !== 11) return `${count} песня`;
    if ([2, 3, 4].includes(mod10) && ![12, 13, 14].includes(mod100)) return `${count} песни`;
    return `${count} песен`;
}

function pluralizeTales(count) {
    const mod10 = count % 10;
    const mod100 = count % 100;
    if (mod10 === 1 && mod100 !== 11) return `${count} сказка`;
    if ([2, 3, 4].includes(mod10) && ![12, 13, 14].includes(mod100)) return `${count} сказки`;
    return `${count} сказок`;
}

// Main App Component
export default function App({initialView, skazkiAction, skazkiTaleId}) {
    // Get page config based on current URL (needed early for preSelectedPersonaId)
    const pageConfig = getPageConfig();

    const {
        credits,
        musicCredits,
        skazkiCredits,
        history,
        musicHistory,
        skazkiHistory,
        subscription,
        linkedTelegramId,
        linkedMaxId,
        refetch: fetchAccountStatus
    } = useAccountStatus();
    const [plans, setPlans] = useState({subscriptions: [], topups: []});
    const [activeView, setActiveView] = useState(initialView || 'video'); // 'video' | 'music' | 'skazki' | 'myContent'
    const skazkiAudioState = useSkazkiAudio();
    const [activeSkazkiTaleId, setActiveSkazkiTaleId] = useState(skazkiTaleId || null);
    useEffect(() => {
        if (activeView === 'skazki') trackEvent('skazki_tab_open');
    }, [activeView]);
    const [preSelectedPersona, setPreSelectedPersona] = useState(null);
    const [preSelectedText, setPreSelectedText] = useState('');
    const [playingVideo, setPlayingVideo] = useState(null);
    const [isGenerating, setIsGenerating] = useState(false);
    const [generatingPersona, setGeneratingPersona] = useState(null);
    const [generatedVideo, setGeneratedVideo] = useState(null);
    const [statusTextIndex, setStatusTextIndex] = useState(0);
    const [trackingGenerationId, setTrackingGenerationId] = useState(null);
    // Use preSelectedPersonaId from config if available
    const [selectedPersonaId, setSelectedPersonaId] = useState(
        pageConfig.preSelectedPersonaId || PERSONAS[0].id
    );
    const [isExampleVideoPlaying, setIsExampleVideoPlaying] = useState(false);
    const [postPurchaseContext, setPostPurchaseContext] = useState(null); // null | 'video' | 'music' | 'skazki'
    const stopExampleVideoRef = useRef(null);
    const pricingRef = useRef(null);
    const mainCardRef = useRef(null);

    // Set meta tags
    useEffect(() => {
        document.title = pageConfig.title;
        document.querySelector('meta[name="description"]')?.setAttribute('content', pageConfig.description);
    }, [pageConfig]);

    // Fetch pricing plans from API
    useEffect(() => {
        getPlans().then(data => {
            const mapPlan = (p) => ({
                id: p.id,
                name: p.name,
                videos: p.videos_count,
                songs: p.music_credits,
                songs_text: pluralizeSongs(p.music_credits),
                tales: p.skazki_credits || 0,
                tales_text: pluralizeTales(p.skazki_credits || 0),
                price: p.price_rub,
                oldPrice: p.old_price_rub,
                discount: p.discount_percent || null,
                kind: p.kind || 'topup',
                billingPeriod: p.billing_period || null,
            });
            setPlans({
                subscriptions: (data.subscriptions || []).map(mapPlan),
                topups: (data.topups || []).map(mapPlan),
            });
        }).catch(console.error);
    }, []);

    // Cycle through status texts when generating
    useEffect(() => {
        if (!isGenerating) {
            setStatusTextIndex(0);
            return;
        }
        const interval = setInterval(() => {
            setStatusTextIndex(prev => (prev + 1) % GENERATION_STATUS_TEXTS.length);
        }, 10000);
        return () => clearInterval(interval);
    }, [isGenerating]);

    // Track generation status from polled account data
    useEffect(() => {
        const pendingGen = history.find(g => g.status === 'pending' || g.status === 'processing');

        if (pendingGen) {
            if (!isGenerating) {
                const persona = PERSONAS.find(p => p.id === pendingGen.celeb_id);
                setIsGenerating(true);
                setGeneratingPersona(persona);
                setTrackingGenerationId(pendingGen.id);
            }
        } else if (trackingGenerationId) {
            const completedGen = history.find(g => g.id === trackingGenerationId);
            setIsGenerating(false);
            setTrackingGenerationId(null);

            if (completedGen?.status === 'completed' && completedGen?.video_url) {
                setGeneratedVideo(completedGen.video_url);
            } else if (completedGen?.status === 'failed') {
                showError('Ошибка: Не удалось сгенерировать видео');
            }
        }
    }, [history, isGenerating, trackingGenerationId]);

    const handleShareVideo = async (videoUrl) => {
        if (navigator.share) {
            try {
                await navigator.share({
                    title: 'Видеопоздравление от ИИ-звезды',
                    text: 'Посмотри мое видеопоздравление от ИИ-звезды!',
                    url: videoUrl
                });
            } catch (error) {
                if (error.name !== 'AbortError') {
                    console.error('Share failed:', error);
                }
            }
        }
    };

    // Callbacks for MainCard
    const handleGenerationStart = (persona) => {
        setIsGenerating(true);
        setGeneratingPersona(persona);
    };

    const handleGenerationComplete = (videoUrl) => {
        setIsGenerating(false);
        if (videoUrl) {
            setGeneratedVideo(videoUrl);
        }
    };

    const handleMyVideosClick = () => {
        setActiveView('myContent');
        setPreSelectedPersona(null);
        window.scrollTo({top: 0, behavior: 'smooth'});
    };

    const handlePricingClick = () => {
        setActiveView('video');
        setPreSelectedPersona(null);
        setTimeout(() => {
            pricingRef.current?.scrollIntoView({behavior: 'smooth'});
        }, 100);
    };

    const handleNoCredits = (persona, text) => {
        trackEvent('see_paywall');
        setPreSelectedPersona(persona);
        setPreSelectedText(text || '');
        setActiveView('video');
        setTimeout(() => {
            pricingRef.current?.scrollIntoView({behavior: 'smooth'});
        }, 100);
    };

    const handleMusicNoCredits = () => {
        trackEvent('see_paywall_music');
        setPreSelectedPersona(null);
        setTimeout(() => {
            pricingRef.current?.scrollIntoView({behavior: 'smooth'});
        }, 100);
    };

    const handleClearPreSelection = () => {
        setPreSelectedPersona(null);
        setPreSelectedText('');
    };

    const handleRegenerate = async (item) => {
        try {
            await createVideoGeneration({
                celebId: item.celeb_id,
                wishText: item.wish_text,
                accountId: ACCOUNT_ID,
            });
            fetchAccountStatus();
        } catch (error) {
            console.error('Regenerate error:', error);
            showError('Ошибка при повторной генерации');
        }
    };

    const handlePurchaseSuccess = () => {
        fetchAccountStatus();

        if (activeView === 'skazki' && activeSkazkiTaleId) {
            // User paid from skazki preview — stay on the preview so they can
            // trigger full generation via the unlock button.
            setPreSelectedPersona(null);
            setPreSelectedText('');
            window.scrollTo({top: 0, behavior: 'smooth'});
            return;
        }

        const ctx = activeView === 'music' ? 'music' : activeView === 'skazki' ? 'skazki' : 'video';
        setPreSelectedPersona(null);
        setPreSelectedText('');
        setPostPurchaseContext(ctx);
    };

    const handlePayForItem = () => {
        // Single-item retries no longer supported as a separate flow — direct user to paywall.
        setActiveView('video');
        setTimeout(() => {
            pricingRef.current?.scrollIntoView({behavior: 'smooth'});
        }, 100);
    };

    // Video Result State
    if (generatedVideo) {
        return (
            <div className="min-h-screen bg-white font-sans flex items-center justify-center p-4">
                <div className="max-w-2xl w-full">
                    <h2 className="text-[42px] font-bold text-brand-dark text-center mb-8 leading-[1.1] tracking-[-0.42px]">Ваше
                        видео готово!</h2>
                    <div className="bg-white rounded-[30px] shadow-2xl overflow-hidden mb-6">
                        <video controls autoPlay className="w-full">
                            <source src={generatedVideo} type="video/mp4"/>
                        </video>
                    </div>
                    <div className="flex flex-wrap gap-4 justify-center">
                        <a
                            href={generatedVideo}
                            download
                            className="px-8 py-4 bg-gradient-to-r from-brand-red via-brand-pink to-brand-orange text-white font-medium rounded-2xl hover:opacity-90 transition shadow-lg text-center"
                        >
                            Скачать видео
                        </a>
                        {typeof navigator !== 'undefined' && navigator.share && (
                            <button
                                onClick={() => handleShareVideo(generatedVideo)}
                                className="md:hidden px-8 py-4 bg-white text-brand-dark font-medium rounded-2xl hover:bg-gray-50 transition shadow-lg border border-gray-200"
                            >
                                Поделиться
                            </button>
                        )}
                        <button
                            onClick={() => {
                                setGeneratedVideo(null);
                            }}
                            className="px-8 py-4 bg-brand-gray text-brand-dark font-medium rounded-2xl hover:bg-gray-200 transition"
                        >
                            Создать ещё
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-white font-sans overflow-x-hidden relative">

            <div className="relative z-10">
                <Header
                    onHomeClick={() => {
                        setActiveView('video');
                        setPreSelectedPersona(null);
                        window.scrollTo({top: 0, behavior: 'smooth'});
                    }}
                />

                {/* Navigation — inline on mobile (below logo), fixed top-right on md+ */}
                <div
                    className="md:fixed md:top-4 md:z-40 md:right-4 lg:right-[calc((100vw-1110px)/2)] w-full md:w-auto px-4 md:px-0 -mt-2 mb-2 md:mt-0 md:mb-0">
                    <div
                        className="bg-gray-100 p-1 rounded-full md:shadow-sm">
                        <div className="flex gap-1">
                            <button
                                onClick={() => {
                                    setActiveView('video');
                                    setPreSelectedPersona(null);
                                    window.scrollTo({top: 0, behavior: 'smooth'});
                                }}
                                className={`flex-1 md:flex-none px-4 py-2.5 md:px-5 md:py-2.5 rounded-full font-medium text-sm transition ${
                                    activeView === 'video'
                                        ? 'bg-gradient-to-r from-brand-red via-brand-pink to-brand-orange text-white shadow-sm'
                                        : 'bg-transparent text-brand-dark hover:text-brand-red'
                                }`}
                            >
                                Видео
                            </button>
                            <button
                                onClick={() => {
                                    setActiveView('music');
                                    window.scrollTo({top: 0, behavior: 'smooth'});
                                }}
                                className={`flex-1 md:flex-none px-4 py-2.5 md:px-5 md:py-2.5 rounded-full font-medium text-sm transition ${
                                    activeView === 'music'
                                        ? 'bg-gradient-to-r from-brand-red via-brand-pink to-brand-orange text-white shadow-sm'
                                        : 'bg-transparent text-brand-dark hover:text-brand-red'
                                }`}
                            >
                                Песни
                            </button>
                            <button
                                onClick={() => {
                                    setActiveView('skazki');
                                    window.scrollTo({top: 0, behavior: 'smooth'});
                                }}
                                className={`flex-1 md:flex-none px-4 py-2.5 md:px-5 md:py-2.5 rounded-full font-medium text-sm transition ${
                                    activeView === 'skazki'
                                        ? 'bg-gradient-to-r from-brand-red via-brand-pink to-brand-orange text-white shadow-sm'
                                        : 'bg-transparent text-brand-dark hover:text-brand-red'
                                }`}
                            >
                                Сказки
                            </button>
                            <button
                                onClick={handleMyVideosClick}
                                className={`flex-1 md:flex-none px-4 py-2.5 md:px-5 md:py-2.5 rounded-full font-medium text-sm transition flex items-center justify-center gap-2 ${
                                    activeView === 'myContent'
                                        ? 'bg-gradient-to-r from-brand-red via-brand-pink to-brand-orange text-white shadow-sm'
                                        : 'bg-transparent text-brand-dark hover:text-brand-red'
                                }`}
                            >
                                Заказы
                                {(credits > 0 || musicCredits > 0 || skazkiCredits > 0) && (
                                    <span className={`text-s px-1.5 py-0.5 rounded-full ${
                                        activeView === 'myContent'
                                            ? 'bg-white text-brand-red'
                                            : 'bg-gradient-to-r from-brand-red to-brand-orange text-white'
                                    }`}>
                                        {credits + musicCredits + skazkiCredits}
                                    </span>
                                )}
                            </button>
                        </div>
                    </div>
                </div>

                {activeView === 'myContent' ? (
                    <main className="pt-6">
                        <MyContentPage
                            history={history}
                            musicHistory={musicHistory}
                            skazkiTales={skazkiHistory}
                            credits={credits}
                            musicCredits={musicCredits}
                            skazkiCredits={skazkiCredits}
                            subscription={subscription}
                            plans={plans}
                            linkedTelegramId={linkedTelegramId}
                            linkedMaxId={linkedMaxId}
                            onPlay={(url) => setPlayingVideo(url)}
                            onPay={handlePayForItem}
                            fetchAccountStatus={fetchAccountStatus}
                            onGenerateClick={() => {
                                setActiveView('video');
                                window.scrollTo({top: 0, behavior: 'smooth'});
                            }}
                            onBuyCredits={() => {
                                setActiveView('video');
                                setTimeout(() => {
                                    pricingRef.current?.scrollIntoView({behavior: 'smooth'});
                                }, 100);
                            }}
                            onRegenerate={handleRegenerate}
                            onMusicClick={() => {
                                setActiveView('music');
                                window.scrollTo({top: 0, behavior: 'smooth'});
                            }}
                            onSkazkiOpen={(taleId) => {
                                setActiveSkazkiTaleId(taleId);
                                setActiveView('skazki');
                                window.history.pushState({}, '', `/skazki/s/${taleId}`);
                                window.scrollTo({top: 0, behavior: 'smooth'});
                            }}
                            onSkazkiClick={() => {
                                setActiveView('skazki');
                                window.scrollTo({top: 0, behavior: 'smooth'});
                            }}
                        />
                    </main>
                ) : activeView === 'skazki' ? (
                    <main className="pt-6 skazki-page">
                        <SkazkiCard
                            initialAction={skazkiAction}
                            initialTaleId={activeSkazkiTaleId}
                            skazkiCredits={skazkiCredits}
                            plans={plans}
                            subscription={subscription}
                            onRefreshAccount={fetchAccountStatus}
                        />
                    </main>
                ) : activeView === 'music' ? (
                    <main className="pt-6">
                        <HeroSection config={MUSIC_HERO}/>

                        <MusicCard
                            musicCredits={musicCredits}
                            musicHistory={musicHistory}
                            onNoCredits={handleMusicNoCredits}
                        />

                        <div ref={pricingRef}>
                            <Paywall
                                plans={plans}
                                subscription={subscription}
                                credits={credits}
                                musicCredits={musicCredits}
                                skazkiCredits={skazkiCredits}
                                linkedTelegramId={linkedTelegramId}
                                linkedMaxId={linkedMaxId}
                                context="music"
                                preSelectedPersona={preSelectedPersona}
                                preSelectedText={preSelectedText}
                                onPurchaseSuccess={handlePurchaseSuccess}
                                onClearPreSelection={handleClearPreSelection}
                                onExampleVideoPlay={setIsExampleVideoPlaying}
                                stopExampleVideoRef={stopExampleVideoRef}
                            />
                        </div>

                        <WhyPerfectSection config={MUSIC_WHY_PERFECT}/>

                        <FiveReasonsSection reasons={MUSIC_FIVE_REASONS}/>
                    </main>
                ) : (
                    <main className="pt-6">
                        <HeroSection config={pageConfig.hero}/>

                        <div ref={mainCardRef}>
                            <MainCard
                                credits={credits}
                                selectedPersonaId={selectedPersonaId}
                                onPersonaChange={setSelectedPersonaId}
                                onGenerationStart={handleGenerationStart}
                                onGenerationComplete={handleGenerationComplete}
                                onAccountStatusUpdate={fetchAccountStatus}
                                onNoCredits={handleNoCredits}
                                hideBack={false}
                                isExampleVideoPlaying={isExampleVideoPlaying}
                                onStopExampleVideo={() => stopExampleVideoRef.current?.()}
                            />
                        </div>

                        <div ref={pricingRef}>
                            <Paywall
                                plans={plans}
                                subscription={subscription}
                                credits={credits}
                                musicCredits={musicCredits}
                                skazkiCredits={skazkiCredits}
                                linkedTelegramId={linkedTelegramId}
                                linkedMaxId={linkedMaxId}
                                context="video"
                                preSelectedPersona={preSelectedPersona}
                                preSelectedText={preSelectedText}
                                onPurchaseSuccess={handlePurchaseSuccess}
                                onClearPreSelection={handleClearPreSelection}
                                onExampleVideoPlay={setIsExampleVideoPlaying}
                                stopExampleVideoRef={stopExampleVideoRef}
                            />
                        </div>

                        <WhyPerfectSection config={pageConfig.whyPerfect}/>

                        <FiveReasonsSection reasons={pageConfig.fiveReasons}/>

                        <StarReviewSection
                            personaId={selectedPersonaId || undefined}
                            personaName={selectedPersonaId ? PERSONAS.find(p => p.id === selectedPersonaId)?.name : undefined}
                            personaImage={selectedPersonaId ? PERSONAS.find(p => p.id === selectedPersonaId)?.image : undefined}
                        />

                        {/* Blog articles */}
                        <div className="max-w-[1110px] mx-auto px-4 pb-10 md:pb-16">
                            <h2 className="text-[28px] md:text-[42px] font-bold text-brand-dark leading-[1.1] tracking-[-0.42px] mb-8 text-center">
                                Полезные{' '}
                                <span className="text-gradient">статьи</span>
                            </h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {blogArticles.slice(0, 4).map(article => (
                                    <a
                                        key={article.slug}
                                        href={`/blog/${article.slug}/`}
                                        className="group block rounded-2xl border border-brand-dark/10 p-5 hover:border-brand-red/30 hover:shadow-md transition"
                                    >
                                        <h3 className="font-bold text-brand-dark group-hover:text-brand-red transition leading-snug">
                                            {article.title}
                                        </h3>
                                        <p className="text-sm text-brand-dark/60 mt-2 leading-relaxed">{article.excerpt}</p>
                                    </a>
                                ))}
                            </div>
                            <div className="text-center mt-6">
                                <a
                                    href="/blog/"
                                    className="inline-block px-6 py-3 rounded-xl bg-gradient-to-r from-brand-red via-brand-pink to-brand-orange text-white text-sm font-medium hover:opacity-95 transition"
                                >
                                    Все статьи блога
                                </a>
                            </div>
                        </div>
                    </main>
                )}

                <Footer
                    onMyVideosClick={handleMyVideosClick}
                    onGenerateClick={() => {
                        setActiveView('video');
                        setPreSelectedPersona(null);
                        window.scrollTo({top: 0, behavior: 'smooth'});
                    }}
                />
            </div>

            {/* Generation In Progress Floating Modal */}
            {isGenerating && (
                <div
                    className="fixed bottom-6 right-6 z-50 w-[320px] bg-white rounded-[20px] shadow-2xl overflow-hidden">
                    <div className="p-5">
                        <div className="flex items-center justify-between mb-4">
                            <span
                                className="text-lg font-medium text-brand-dark tracking-[-0.18px]">Генерируем...</span>
                            <button
                                onClick={() => setIsGenerating(false)}
                                className="w-8 h-8 rounded-full bg-brand-gray flex items-center justify-center text-brand-dark/60 hover:bg-gray-200 transition"
                            >
                                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                                    <path d="M1 1L13 13M1 13L13 1" stroke="currentColor" strokeWidth="2"
                                          strokeLinecap="round"/>
                                </svg>
                            </button>
                        </div>

                        <div className="relative rounded-[12px] overflow-hidden mb-4">
                            <img
                                src={generatingPersona?.image}
                                alt={generatingPersona?.name}
                                className="w-full h-[180px] object-cover"
                            />
                            <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                                <div
                                    className="w-12 h-12 border-3 border-white/30 border-t-white rounded-full animate-spin"></div>
                            </div>
                        </div>

                        <p className="text-base font-medium text-brand-dark text-center leading-[1.3] tracking-[-0.16px] mb-4 whitespace-pre-line">
                            {GENERATION_STATUS_TEXTS[statusTextIndex]}
                        </p>
                    </div>
                </div>
            )}

            {/* Post-Purchase Suggestion Modal */}
            {postPurchaseContext && (
                <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
                    <div className="bg-white rounded-[24px] p-8 max-w-md w-full shadow-2xl text-center">
                        <div
                            className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-r from-green-400 to-green-500 flex items-center justify-center">
                            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3"
                                 strokeLinecap="round" strokeLinejoin="round">
                                <path d="M20 6L9 17l-5-5"/>
                            </svg>
                        </div>

                        <h3 className="text-2xl font-bold text-brand-dark mb-2">
                            Оплата прошла!
                        </h3>
                        <p className="text-brand-dark/60 mb-6">
                            Кредиты зачислены на ваш баланс.
                            Создайте {postPurchaseContext === 'music' ? 'песню' : postPurchaseContext === 'skazki' ? 'сказку' : 'видеопоздравление'} прямо
                            сейчас!
                        </p>

                        <button
                            onClick={() => {
                                const ctx = postPurchaseContext;
                                setPostPurchaseContext(null);
                                setActiveView(ctx);
                                window.scrollTo({top: 0, behavior: 'smooth'});
                            }}
                            className="w-full py-4 rounded-2xl bg-gradient-to-r from-brand-red via-brand-pink to-brand-orange text-white text-base font-semibold hover:opacity-95 transition mb-3"
                        >
                            {postPurchaseContext === 'music' ? 'Создать песню' : postPurchaseContext === 'skazki' ? 'Создать сказку' : 'Создать видео'}
                        </button>

                        {subscription && ['active', 'cancel_at_period_end', 'failed'].includes(subscription.status) && (
                            <div className="border-t border-brand-dark/10 pt-4 mt-2 mb-2">
                                <p className="text-xs text-brand-dark/60 mb-3">
                                    Привяжите аккаунт к боту, чтобы не потерять доступ
                                </p>
                                <div className="flex gap-2 justify-center flex-wrap">
                                    <LinkAccountButton platform="telegram" isLinked={!!linkedTelegramId} size="sm"/>
                                    <LinkAccountButton platform="max" isLinked={!!linkedMaxId} size="sm"/>
                                </div>
                            </div>
                        )}

                        <button
                            onClick={() => {
                                setPostPurchaseContext(null);
                                setActiveView('myContent');
                                window.scrollTo({top: 0, behavior: 'smooth'});
                            }}
                            className="w-full py-3 text-brand-dark/60 hover:text-brand-dark text-sm font-medium transition"
                        >
                            Перейти к заказам
                        </button>
                    </div>
                </div>
            )}

            <VideoModal videoUrl={playingVideo} onClose={() => setPlayingVideo(null)}/>
        </div>
    );
}
